import { Controller } from "@hotwired/stimulus"

// Custom edit's "Find a part": searches the catalogue (figures#lookup) as
// you type and lists matching figures to use as the part's donor, then the
// words themselves as a plain part. Picking one fills in the new-part form
// below it (name, maker, donor) and moves to its name.
export default class extends Controller {
  static targets = ["input", "results", "form"]
  static values = { url: String }

  search() {
    clearTimeout(this.timer)
    const query = this.inputTarget.value.trim()
    if (query.length < 2) return this.close()
    this.timer = setTimeout(() => this.fetch(query), 200)
  }

  async fetch(query) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok || query !== this.inputTarget.value.trim()) return
    this.show(query, await response.json())
  }

  show(query, figures) {
    const rows = figures.map((figure) => this.row(figure.image, figure.name, figure.brand, [figure.detail, "catalog figure · use as donor"].filter(Boolean).join(" · "),
      () => this.pick({ name: figure.name, maker: figure.brand, donor: figure.label })))
    rows.push(this.row(null, `“${query}”`, null, "add as a plain part", () => this.pick({ name: query })))
    this.resultsTarget.replaceChildren(...rows)
    this.resultsTarget.hidden = false
    this.active = -1
  }

  row(image, name, brand, note, onPick) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "cx-result"
    button.setAttribute("role", "option")
    const pic = document.createElement(image ? "img" : "span")
    pic.className = "cx-result-pic"
    if (image) { pic.src = image; pic.alt = "" }
    const label = document.createElement("b")
    label.textContent = name
    button.append(pic, label)
    if (brand) {
      const chip = document.createElement("span")
      chip.className = "ui-chip brand"
      chip.textContent = brand
      button.append(chip)
    }
    const hint = document.createElement("span")
    hint.className = "ui-note cx-result-note"
    hint.textContent = note
    button.append(hint)
    button.addEventListener("click", onPick)
    return button
  }

  pick({ name, maker, donor }) {
    const form = this.formTarget
    const set = (selector, value) => {
      const field = form.querySelector(selector)
      if (field && value != null) { field.value = value; field.dispatchEvent(new Event("input", { bubbles: true })) }
    }
    set("input[name='custom_part[display_name]']", name)
    set("input[name='custom_part[maker]']", maker)
    set("input[name='custom_part[donor]']", donor)
    this.close()
    this.inputTarget.value = ""
    const nameField = form.querySelector("input[name='custom_part[display_name]']")
    nameField?.scrollIntoView({ block: "center", behavior: "smooth" })
    nameField?.focus({ preventScroll: true })
    nameField?.select()
  }

  // Arrow keys move through the results; Enter picks; Escape closes.
  key(event) {
    const rows = [...this.resultsTarget.querySelectorAll(".cx-result")]
    if (event.key === "Escape") return this.close()
    if (!rows.length || this.resultsTarget.hidden) return
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      this.active = (this.active + (event.key === "ArrowDown" ? 1 : -1) + rows.length) % rows.length
      rows.forEach((row, i) => row.classList.toggle("on", i === this.active))
    } else if (event.key === "Enter") {
      event.preventDefault()
      rows[Math.max(this.active, 0)].click()
    }
  }

  close() {
    this.resultsTarget.hidden = true
    this.resultsTarget.replaceChildren()
  }
}
